/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classnames from 'classnames';

import { FormValidationStatus, TextField } from '@coursera/cds-core';

import { InvalidInputIcon } from 'bundles/payments-common/lib/validate';

import _t from 'i18n!nls/payments';

import 'css!./__styles__/InputFormGroup';

type Props = {
  labelText: string | JSX.Element;
  subLabelText?: string | JSX.Element;
  placeholder: string;
  name: string;
  cols?: string;
  htmlAttributes?: any;
  onChange?: (x0?: string | null | undefined) => void;
  onValidate?: ((x0: React.SyntheticEvent<any> | React.KeyboardEvent<any>) => void) | null;
  validateFn?: (x0: string) => boolean;
  defaultValue?: string;
  disabled?: boolean;
  inputId: string;
  describedBy?: string;
  isFormSubmitted?: boolean;
  isVerticallyAligned?: boolean;
  // TODO remove `shouldAlwaysTriggerChange` once all forms validate invalid inputs
  shouldAlwaysTriggerChange?: boolean;
  hideErrorIcon?: boolean;
  formatFn?: (x0: string) => string;
  isRequired?: boolean;
  showCdsDesign?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

type State = {
  valid: boolean;
  attempted: boolean;
  value?: string;
};

/**
 * Builds a bt3-input-form-group for payments.
 * Takes most of the standard html input attributes and
 * takes a content validation function.
 */

const styles = {
  formContainer: css`
    .bt3-form-group {
      margin-bottom: 15px;
    }

    .bt3-has-success .bt3-radio {
      color: var(--cds-color-feedback-success);
    }

    .bt3-has-success .bt3-form-control {
      border-color: var(--cds-color-feedback-success);
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-success .bt3-form-control:focus {
      border-color: var(--cds-color-feedback-success);
      box-shadow:
        inset 0 1px 1px rgb(0 0 0 / 7.5%),
        0 0 6px var(--cds-color-green-500);
    }

    .bt3-has-error .bt3-radio {
      color: var(--cds-color-red-800);
    }

    .bt3-has-error .bt3-form-control {
      border-color: var(--cds-color-red-800);
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-error .bt3-form-control:focus {
      border-color: var(--cds-color-feedback-error-hover);
      box-shadow:
        inset 0 1px 1px rgb(0 0 0 / 7.5%),
        0 0 6px var(--cds-color-red-500);
    }
  `,
  textFieldInput: css`
    && {
      label {
        padding: 0;
        margin-bottom: 16px;
        text-transform: capitalize;
      }

      .cds-text-field-root {
        margin-top: unset;
      }

      .cds-input-input {
        padding: var(--cds-spacing-150) !important;
        background: inherit !important;
        border-color: var(--cds-color-neutral-stroke-primary) !important;
      }
    }
  `,

  validationError: css`
    background-color: transparent !important;
    padding-left: 0 !important;
  `,
};
class InputFormGroup extends React.Component<Props, State> {
  static defaultProps = {
    cols: '7',
    htmlAttributes: {},
    onChange: () => {},
    validateFn: (value: string) => !!value,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      valid: false,
      attempted: false,
      value: props.defaultValue,
    };
  }

  componentDidMount() {
    const { defaultValue } = this.props;

    if (defaultValue) {
      this.checkValidity();
    }
  }

  onKeyUp = (e: React.SyntheticEvent<any>) => {
    const { onValidate, validateFn } = this.props;
    // @ts-expect-error TSMIGRATION
    const { value } = e.target;

    // @ts-expect-error TSMIGRATION
    if (onValidate && value && validateFn(value)) {
      onValidate(e);
    }
  };

  onInputChange = (e: React.SyntheticEvent<any>): void => {
    const DELETE_CODE = 46;
    const { formatFn } = this.props;

    // @ts-expect-error TSMIGRATION
    if (formatFn && e.target.value && e.keyCode !== DELETE_CODE) {
      // @ts-expect-error TSMIGRATION
      e.target.value = formatFn(e.target.value);
    }
  };

  onBlur = (e: React.ChangeEvent<any>): void => {
    this.setState({ value: e.target.value }, this.checkValidity);
    this.props.onBlur?.();
  };

  onFocus = () => {
    this.props.onFocus?.();
  };

  /**
   * Check validity of this form group.
   * @param {boolean} [options.attempt] - Count this function running as an attempt?
   */
  checkValidity = (options?: { attempt: boolean }): boolean => {
    const { value, attempted } = this.state;
    const { validateFn, onChange, shouldAlwaysTriggerChange } = this.props;
    // Only want to show invalidity (red color) if the input has been
    // attempted and is invalid.
    const valid = value && validateFn ? validateFn(value) : false;
    this.setState({
      valid,
      attempted: (options && options.attempt) || attempted || (value ? value.length > 0 : false),
    });

    if (onChange) {
      onChange(valid || shouldAlwaysTriggerChange ? value : null);
    }

    return valid;
  };

  render() {
    const {
      placeholder,
      labelText,
      subLabelText,
      name,
      defaultValue,
      inputId,
      disabled,
      describedBy,
      isFormSubmitted,
      isVerticallyAligned,
      hideErrorIcon,
      cols,
      htmlAttributes,
      isRequired,
      showCdsDesign,
    } = this.props;

    const { attempted, valid, value } = this.state;
    const hasError = (attempted || isFormSubmitted) && !valid;
    const hasSuccess = (attempted || isFormSubmitted) && valid;

    const formGroupClass = classnames({
      'bt3-form-group': !isVerticallyAligned,
      'bt3-has-success': hasSuccess,
      'bt3-has-error': hasError,
    });

    const inputClass = classnames('larger-font-size', {
      'padded-input': isVerticallyAligned,
    });

    const getValidationStatus = () => {
      if (hasSuccess) {
        return 'success';
      } else if (hasError) {
        return 'error';
      }

      return undefined;
    };

    const input = showCdsDesign ? (
      <>
        <TextField
          id={inputId}
          type="text"
          placeholder={placeholder}
          name={name}
          onInput={this.onInputChange}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          defaultValue={defaultValue}
          disabled={disabled}
          aria-label={String(labelText).toLocaleLowerCase()}
          label={String(labelText).toLocaleLowerCase()}
          inputProps={{
            'aria-required': isRequired,
            'aria-invalid': hasError,
            'aria-describedby': describedBy || `validation-status-${name}`,
            onKeyUp: this.onKeyUp,
          }}
          fullWidth
          css={styles.textFieldInput}
          validationStatus={getValidationStatus()}
          {...htmlAttributes}
        />
        <div aria-live="assertive">
          {hasError && (value?.trim().length ?? 0) <= 1 && (
            <FormValidationStatus
              css={styles.validationError}
              label={_t('Please enter your #{labelText}', { labelText: String(labelText).toLocaleLowerCase() })}
              validationStatus="error"
              hideIcon={true}
              id={describedBy || `validation-status-${name}`}
            />
          )}
        </div>
      </>
    ) : (
      <>
        <input
          id={inputId}
          type="text"
          placeholder={placeholder}
          name={name}
          onInput={this.onInputChange}
          onKeyUp={this.onKeyUp}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          defaultValue={defaultValue}
          disabled={disabled}
          aria-describedby={describedBy || `validation-status-${name}`}
          className={inputClass}
          {...htmlAttributes}
          aria-invalid={hasError}
          aria-required={isRequired}
        />
        <div aria-live="assertive">
          {hasError && (value?.trim().length ?? 0) <= 1 && (
            <FormValidationStatus
              css={styles.validationError}
              label={_t('Please enter your #{labelText}', { labelText: String(labelText).toLocaleLowerCase() })}
              validationStatus="error"
              hideIcon={true}
              id={describedBy || `validation-status-${name}`}
            />
          )}
        </div>
      </>
    );

    let content;

    if (showCdsDesign) {
      content = <div>{input}</div>;
    } else if (isVerticallyAligned) {
      content = (
        <div className={formGroupClass}>
          {labelText && (
            <label className="label-text" htmlFor={inputId}>
              {labelText}
              {subLabelText && <span className="label-text__sub-label">{subLabelText}</span>}
              {!hideErrorIcon && <InvalidInputIcon hasError={hasError} />}
            </label>
          )}
          {input}
        </div>
      );
    } else {
      content = (
        <div className={formGroupClass}>
          <div className="bt3-col-sm-1" />
          <label className="bt3-col-sm-3" htmlFor={inputId}>
            <b className="body-2-text">{labelText}</b>
            <InvalidInputIcon hasError={hasError} />
          </label>
          <div className={`bt3-col-sm-${cols}`}>{input}</div>
        </div>
      );
    }

    return (
      <div css={styles.formContainer} className="rc-InputFormGroup">
        {content}
      </div>
    );
  }
}

export default InputFormGroup;
